import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, IAppConfig } from '@tcc-mono/shared/app-config';
import { BaseService, Response } from '@tcc-mono/shared/data-access';
import { PurchaseOrder, Ride } from '@tcc-mono/vandijk/shared/data-access/models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RideService extends BaseService<Ride> {
  public constructor(
    _http: HttpClient,
    @Inject(APP_CONFIG) appConfig: IAppConfig
  ) {
    super(_http, appConfig);
    this.path = 'api/rides';
  }

  public addAnimal = (
    rideId: number,
    country_code: string,
    identification_number: string,
    remarks: string,
    is_healthy: boolean
  ) => {
    return this.postChild(
      {
        country_code,
        identification_number,
        remarks,
        is_healthy
      },
      `/${rideId}/add_animal`
    );
  };

  public getRvoInfo = (
    country_code: string,
    identification_number: string,
    animal_species?: string,
  ) => {
    return this.postChild(
      {
        country_code,
        identification_number,
        ...(animal_species ? { animal_species } : {}),
      },
      `/rvo/animal_info`
    );
  };

  public createFromKvsRide(body: { hub_id: number, relation_id: number, kvs_ride_id: string }): Observable<Response<Ride>> {
    return this._http.post<Response<Ride>>(`${this.getUrl(this.path)}/create_from_kvs`, body);
  }

  public refreshKvs(rideId: number): Observable<unknown> {
    return this.postChild<unknown>(null, `/${rideId}/ride_to_kvs`);
  }

  public scanningDone(rideId: number, override = false): Observable<unknown> {
    return this._http.post<unknown>(
      this.getUrl(`${this.path}/${rideId}/scanning_done`),
      null,
      { params: override ? { override: 'true' } : {} }
    );
  }

  public resetKvs(rideId: number): Observable<unknown> {
    return this.postChild<unknown>(null, `/${rideId}/reset_kvs`);
  }

  public forceCloseKvsErrors(rideId: number): Observable<unknown> {
    return this.postChild<unknown>(null, `/${rideId}/force_close_kvs_errors`);
  }

  public getPurchaseOrders(rideId: number): Observable<PurchaseOrder[]> {
    return this.getChild<Response<PurchaseOrder[]>>(rideId, 'purchase_orders').pipe(
      map((response) => response.data ?? [])
    );
  }

  public markAnimalKvsRegistered = (
    rideId: PropertyKey,
    animalId: PropertyKey
  ): Observable<unknown> => {
    return this.postChild<unknown>(
      null,
      `/${rideId.toString()}/animals/${animalId.toString()}/mark_kvs_registered`
    );
  };

  public deleteAnimal = (
    rideId: PropertyKey,
    animalId: PropertyKey,
    override: boolean = false
  ): Observable<unknown> => {
    const url = this.getUrl(`${this.path}/${rideId.toString()}/animals/${animalId.toString()}`);
    return this._http.delete<unknown>(url, {
      params: override ? { override: 'true' } : {}
    });
  };
}
